import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Folder, Image as ImageIcon, ChevronRight, Loader2, Home, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const IMAGE_EXTS = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.svg', '.avif', '.bmp'];

const isImage = (name = '') => IMAGE_EXTS.some((ext) => name.toLowerCase().endsWith(ext));
const fullUrl = (url) => (url?.startsWith('http') ? url : `${BACKEND_URL}${url}`);

/**
 * Reusable media browser for the shared iDrive e2 bucket.
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   onSelect: (relativeUrl, fileObj) => void
 *   title?: string
 *   imagesOnly?: boolean (default true) - only show image files
 */
export const MediaPickerModal = ({ open, onClose, onSelect, title = 'Select Media', imagesOnly = true }) => {
  const [prefix, setPrefix] = useState('');
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem('token');

  const fetchMedia = useCallback(async (targetPrefix) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/storage/browse`, {
        params: { prefix: targetPrefix || '' },
        headers: { Authorization: `Bearer ${token}` },
      });
      setFolders(res.data.folders || []);
      const returnedFiles = res.data.files || [];
      setFiles(imagesOnly ? returnedFiles.filter((f) => isImage(f.filename)) : returnedFiles);
      setPrefix(res.data.prefix || '');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load media. Is storage configured?');
      setFolders([]);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [token, imagesOnly]);

  useEffect(() => {
    if (open) {
      fetchMedia('');
    }
  }, [open, fetchMedia]);

  // Build breadcrumb segments from the current prefix
  const segments = prefix ? prefix.replace(/\/$/, '').split('/') : [];
  const goToSegment = (index) => {
    const newPrefix = segments.slice(0, index + 1).join('/') + '/';
    fetchMedia(newPrefix);
  };

  const handleSelect = (file) => {
    onSelect?.(file.url, file);
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="media-picker-modal">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Browse and select an image from your iDrive e2 media bucket.</DialogDescription>
        </DialogHeader>

        {/* Breadcrumbs */}
        <div className="flex items-center gap-1 flex-wrap text-sm border-b pb-3">
          <button
            className="flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100 text-slate-600"
            onClick={() => fetchMedia('')}
            data-testid="media-breadcrumb-root"
          >
            <Home className="w-4 h-4" /> Root
          </button>
          {segments.map((seg, idx) => (
            <span key={idx} className="flex items-center gap-1">
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <button
                className="px-2 py-1 rounded hover:bg-slate-100 text-slate-600 capitalize"
                onClick={() => goToSegment(idx)}
              >
                {seg}
              </button>
            </span>
          ))}
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            onClick={() => fetchMedia(prefix)}
            data-testid="media-refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto py-2">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              {/* Folders */}
              {folders.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 mb-4">
                  {folders.map((folder) => (
                    <button
                      key={folder.prefix}
                      onClick={() => fetchMedia(folder.prefix)}
                      className="flex flex-col items-center gap-2 p-3 rounded-lg border border-slate-200 hover:border-[#6e2ea8] hover:bg-slate-50 transition-colors"
                      data-testid={`media-folder-${folder.name}`}
                    >
                      <Folder className="w-10 h-10 text-[#b9893d]" />
                      <span className="text-xs text-slate-600 truncate w-full text-center">{folder.name}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Files */}
              {files.length > 0 ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {files.map((file) => (
                    <button
                      key={file.key}
                      onClick={() => handleSelect(file)}
                      className="group relative aspect-square rounded-lg overflow-hidden border border-slate-200 hover:border-[#6e2ea8] hover:ring-2 hover:ring-[#6e2ea8]/30 transition-all bg-white"
                      title={file.filename}
                      data-testid={`media-file-${file.filename}`}
                    >
                      <img
                        src={fullUrl(file.url)}
                        alt={file.filename}
                        className="w-full h-full object-contain p-1"
                        loading="lazy"
                      />
                      <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] px-1 py-0.5 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                        {file.filename}
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                folders.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <ImageIcon className="w-12 h-12 mb-3" />
                    <p className="text-sm">No images in this folder</p>
                  </div>
                )
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MediaPickerModal;
